import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  NgZone,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  ContextService,
  Integration,
  SessionService,
  TrackEventService,
} from '@maximizer/core/shared/data-access';
import { Session } from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  NotificationComponent,
  UI_NOTIFICATION,
} from '@maximizer/core/shared/ui/components/notification';
import { ActionArgs, ActionEventType } from '@maximizer/core/shared/ui/config';
import { FormatPipe } from '@maximizer/core/shared/ui/pipes';
import {
  AppDirectoryService,
  EmailAutoSaveSubscriptionCount,
  EmailSubscription,
  EmailSubscriptionBulkResponse,
  EmailSyncService,
  OutlookSyncEntityType,
  UserSyncConfiguration,
  UserSyncConfigurationService,
} from '@maximizer/outlook-sync/data-access';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import {
  catchError,
  EMPTY,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { TrackEventsHelper } from '../classes/track-events-helper';
import { ModuleId } from '../classes/types-and-enums';
import { Labels } from '../models/labels';

export interface IntegrationArguments {
  key: string;
  integration: Integration;
}

interface GridEntryKeysArguments {
  keys: string[];
  moduleId: ModuleId;
}

@Component({
  selector: 'maximizer-outlook-sync-add-entries',
  templateUrl: './add-entries.component.html',
  styleUrl: './add-entries.component.scss',
  providers: [FormatPipe],
  imports: [
    KENDO_BUTTON,
    KENDO_DIALOG,
    NgTemplateOutlet,
    TranslatePipe,
    UI_LOADER,
    UI_NOTIFICATION,
  ],
})
export class AddEntriesComponent extends EmbeddedComponent {
  @HostBinding('class.outlook-sync-ng') outlookSyncClass = true;

  @ViewChild('notificationContainer', { read: ViewContainerRef })
  notificationContainerRef: ViewContainerRef | undefined;

  isSyncNotInstalledDialogOpen = false;
  isEmailSubscriptionLimitDialogOpen = false;
  isEmailSyncDisabledDialogOpen = false;
  isLoading = false;

  labels?: Labels;
  currentModuleId: ModuleId = 'ab';
  entriesRequestingFromUser = 0;

  userSyncConfiguration?: UserSyncConfiguration;
  session?: Session;
  dialogGridEntries?: GridEntryKeysArguments;
  arguments?: IntegrationArguments;

  private _translateService?: TranslateService;

  @ViewChild('successNotification') successNotification!: NotificationComponent;
  @ViewChild('errorNotification') errorNotification!: NotificationComponent;

  private readonly _ngZone = inject(NgZone);
  private readonly _injector = inject(Injector);
  private readonly _context = inject(ContextService);
  private readonly _sessionService = inject(SessionService);
  private readonly _userSyncConfigService = inject(UserSyncConfigurationService);
  private readonly _emailSyncService = inject(EmailSyncService);
  private readonly _appDirectoryService = inject(AppDirectoryService);
  private readonly _trackEvent = inject(TrackEventService);
  private readonly _insightsService = inject(InsightsService);
  private readonly _formatPipe = inject(FormatPipe);

  constructor() {
    super(inject(ElementRef));
  }

  override doAction(
    name: string,
    args?: IntegrationArguments | GridEntryKeysArguments,
  ): void {
    this._ngZone.run(() => {
      this.resetDialogs();
      this.handleAction(name, args);
    });
  }

  private handleAction(
    name: string,
    args?: IntegrationArguments | GridEntryKeysArguments,
  ): void {
    switch (name) {
      case ActionEventType.Initialize:
      case ActionEventType.Show: {
        const integration = args as IntegrationArguments;
        if (integration) {
          this.initialize(args as IntegrationArguments);
          this.doActionRequestSelectedGridEntries();
          if (args && this.isGridEntryArguments(args)) {
            this.processGridEntries(args);
          }
        }
        break;
      }
      case ActionEventType.RequestSelectedGridEntries:
        if (args && this.isGridEntryArguments(args)) {
          this.processGridEntries(args);
        }
        break;
      default:
        super.doAction(name, args);
        break;
    }
  }

  private initialize(args: IntegrationArguments): void {
    this._context.loadIntegration(args.integration);
    this.arguments = args;
  }
  private isGridEntryArguments(
    args: IntegrationArguments | GridEntryKeysArguments,
  ): args is GridEntryKeysArguments {
    return 'keys' in args;
  }

  private processGridEntries(args: GridEntryKeysArguments): void {
    this.isLoading = true;
    this.resetDialogs();
    const process$ = forkJoin({
      session: this._sessionService.getInfo(),
      labels: this.loadLabels(),
    }).pipe(
      tap(({ labels }) => {
        this.labels = labels;
      }),
      switchMap(({ session }) => {
        if (!args?.keys?.length) {
          return this.handleFailure('selectAtLeastOneEntry');
        }

        this.currentModuleId = args.moduleId;
        this.entriesRequestingFromUser = args.keys.length;
        const mapped = this.mapKeysToSubscriptions(args.keys);
        return of({ session, mapped });
      }),
      switchMap(({ session, mapped }) =>
        this.fetchSyncConfiguration(session).pipe(
          map((result) => ({ ...result, mapped })),
        ),
      ),
      switchMap(({ config, count, mapped }) => {
        if (!config) {
          return EMPTY;
        }
        if (!config.emailsSync.enabled) {
          this.isEmailSyncDisabledDialogOpen = true;
          return EMPTY;
        }
        if (count.currentCount >= count.maximumAllowed) {
          this.isEmailSubscriptionLimitDialogOpen = true;
          return EMPTY;
        }
        TrackEventsHelper.outlookSyncEvent(this._trackEvent, 'started');

        return of({ config, mapped });
      }),
      switchMap(({ config, mapped }) =>
        this.insertBulkSubscriptions(config, mapped),
      ),
      tap((result) => this.handleSuccess(result)),
      catchError((error) => {
        this.resetDialogs();
        return this.handleFailure('unexpectedError', error);
      }),
      finalize(() => {
        this.isLoading = false;
      }),
    );

    this.addSubscription(process$.subscribe());
  }

  private resetDialogs(): void {
    this.isEmailSyncDisabledDialogOpen = false;
    this.isEmailSubscriptionLimitDialogOpen = false;
    this.isSyncNotInstalledDialogOpen = false;
  }

  private trackDialogClosed(): void {
    if (
      !this.isEmailSubscriptionLimitDialogOpen &&
      !this.isEmailSyncDisabledDialogOpen &&
      !this.isSyncNotInstalledDialogOpen
    ) {
      return;
    }

    let location = 'sync-not-installed';
    if (this.isEmailSubscriptionLimitDialogOpen) {
      location = 'email-limit';
    } else if (this.isEmailSyncDisabledDialogOpen) {
      location = 'sync-disabled';
    }

    TrackEventsHelper.outlookSyncEvent(this._trackEvent, 'closed', {
      location,
    });
  }

  private mapKeysToSubscriptions(keys: string[]): EmailSubscription[] {
    return keys.map((key) => ({
      maximizerEntityType: OutlookSyncEntityType.AbEntry,
      maximizerKey: key,
      enabled: true,
    }));
  }

  private loadLabels(): Observable<Labels> {
    // Injecting TranslateService here to ensure the translate factory is evaluated after
    // the integration has been loaded into the context. Do not move the injection to the constructor.
    this._translateService = this._injector.get(TranslateService);
    return this._translateService.get(['outlookSync']);
  }
  private fetchSyncConfiguration(session: Session): Observable<{
    config: UserSyncConfiguration;
    count: EmailAutoSaveSubscriptionCount;
  }> {
    if (!session?.user?.id || !session.workspace) {
      console.warn(
        'FetchSyncConfiguration: Invalid session provided, stopping execution',
      );
      return this.handleFailure('unexpectedError');
    }
    this.session = session;

    return this._userSyncConfigService
      .getConfiguration(session.user.id, session.workspace)
      .pipe(
        switchMap((config) => {
          if (!config?.enabled) {
            this.isSyncNotInstalledDialogOpen = true;
            return EMPTY;
          }
          this.userSyncConfiguration = config;
          return this._emailSyncService
            .getEmailAutoSaveSubscriptionCount(config.id)
            .pipe(map((count) => ({ config, count })));
        }),
        catchError((response: HttpErrorResponse) =>
          this.handleFailure('unexpectedError', response),
        ),
      );
  }

  private insertBulkSubscriptions(
    config: UserSyncConfiguration,
    mappedKeys: EmailSubscription[],
  ): Observable<EmailSubscriptionBulkResponse> {
    if (!config?.id || !mappedKeys?.length) {
      return EMPTY;
    }

    return this._emailSyncService
      .insertBulkEmailAutoSaveSubscription(config.id, mappedKeys)
      .pipe(
        catchError((response: HttpErrorResponse) => {
          const errors = response.error.errors;
          if (errors?.max_enabled_subscriptions) {
            this.isEmailSubscriptionLimitDialogOpen = true;
            return EMPTY;
          } else {
            return this.handleFailure('unexpectedError', response);
          }
        }),
      );
  }
  private handleSuccess(result: EmailSubscriptionBulkResponse | null): void {
    if (!result) {
      this.handleFailure('unexpectedError', new Error('Missing result'));
      return;
    }

    const message = this.buildSuccessMessage(result);
    this.notifySuccess(message);
    this.isLoading = false;

    TrackEventsHelper.outlookSyncEvent(this._trackEvent, 'completed');
  }

  private buildSuccessMessage(result: EmailSubscriptionBulkResponse): string {
    const notifications = this.labels?.outlookSync.addEntries.notification;
    if (!notifications) {
      return '';
    }

    const { createdCount, updatedCount } = result;

    if (createdCount > 0 && updatedCount > 0) {
      return this.formatTranslation(
        notifications.createAndUpdate,
        createdCount,
        updatedCount,
      );
    }

    if (createdCount > 0) {
      return this.formatTranslation(notifications.onlyCreate, createdCount);
    }

    return this.formatTranslation(
      notifications.allEntriesAlreadyExist,
      this.entriesRequestingFromUser,
    );
  }

  private notifySuccess(message: string): void {
    this.successNotification.template = message;
    this.successNotification.show();
  }

  private handleFailure(
    labelKey: 'selectAtLeastOneEntry' | 'unexpectedError',
    error?: unknown,
  ): Observable<never> {
    const message =
      this.labels?.outlookSync.addEntries.notification[labelKey] ?? '';

    this.errorNotification.template = message;
    this.errorNotification.show();
    this.isLoading = false;

    if (!error) {
      return EMPTY;
    }

    const exception =
      error instanceof Error || error instanceof HttpErrorResponse
        ? error
        : new Error(message || labelKey);
    const resolvedMessage = this.resolveErrorMessage(
      error,
      message || labelKey,
    );

    this._insightsService.trackException(
      { exception },
      {
        component: 'AddEntriesComponent',
        operation: 'processGridEntries',
        message: resolvedMessage,
        entriesCount: this.entriesRequestingFromUser,
        moduleId: this.currentModuleId,
        httpStatus:
          error instanceof HttpErrorResponse ? error.status : undefined,
      },
    );

    console.error(resolvedMessage, error);

    return EMPTY;
  }

  private formatTranslation(
    translation: string,
    args0: number,
    args1 = 0,
  ): string {
    return this._formatPipe.transform(translation, {
      arg0: args0,
      arg1: args1,
    });
  }

  private doActionRequestSelectedGridEntries(): void {
    const action = new ActionArgs(ActionEventType.RequestSelectedGridEntries);
    this.actionCompleted.emit(action);
  }

  openOutlookStore(): void {
    window.open(
      'https://marketplace.microsoft.com/en-us/product/office/wa200005536?tab=overview',
      '_blank',
      'noopener,noreferrer',
    );
  }
  openAppStore(): void {
    this._sessionService
      .getInfo()
      .pipe(
        take(1),
        switchMap((session) =>
          this._appDirectoryService
            .buildAppStoreFormRequest(session, false)
            .pipe(take(1)),
        ),
        tap((result) => {
          if (!result) {
            return;
          }
          TrackEventsHelper.outlookSyncEvent(this._trackEvent, 'clicked', {
            location: 'app-store',
          });
          this.closeDialog();
        }),
        catchError((error) => this.handleFailure('unexpectedError', error)),
      )
      .subscribe();
  }

  closeDialog(): void {
    this._context.endDialogMode();
    this.trackDialogClosed();
    this.resetDialogs();
  }

  private resolveErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse || error instanceof Error) {
      return error.message ?? fallback;
    }

    if (typeof error === 'object' && error !== null) {
      const maybeMessage = (error as { message?: unknown }).message;
      if (typeof maybeMessage === 'string' && maybeMessage) {
        return maybeMessage;
      }

      const nestedMessage = (error as { error?: { message?: unknown } }).error
        ?.message;
      if (typeof nestedMessage === 'string' && nestedMessage) {
        return nestedMessage;
      }
    }

    return fallback;
  }
}
